/**
 * PORTED — RO-11-04 / #44589.
 * Origin: `excellent/app/_chrome/dock-context.tsx`.
 * Pure React context (no next/*) — openTerminal powers `?dock=terminal`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** Coarse kind, derived from the launch command — drives the tab glyph. */
export type TerminalKind = "agent" | "shell";

/**
 * Whether the launch command is an interactive coding agent (claude / codex /
 * grok / gemini / aider) vs a plain shell. Only used for the tab glyph.
 */
export function deriveKind(command?: string): TerminalKind {
  if (!command) return "shell";
  const first = command.trim().split(/\s+/)[0]?.toLowerCase() ?? "";
  return /^(claude|codex|grok|gemini|aider)$/.test(first) ? "agent" : "shell";
}

export interface TerminalTab {
  /** Local UI id — stable across renames. */
  id: string;
  /** PTY session id, set once the WebSocket reports back. */
  sessionId: string | null;
  /** Display name shown in the tab. Defaults to the shell command. */
  title: string;
  /** Command to run on first launch (only used when sessionId is null). */
  initialCommand?: string;
  /** Agent vs shell — set once at creation, drives the tab glyph. */
  kind: TerminalKind;
  /** Wall-clock ms when the tab was created — the runtime baseline. */
  startedAt: number;
}

interface DockContextValue {
  open: boolean;
  toggle: () => void;
  /** Open the dock (idempotent — never toggles closed), seeding a tab if empty. */
  openTerminal: () => void;
  setOpen: (v: boolean) => void;
  height: number;
  setHeight: (h: number) => void;

  tabs: TerminalTab[];
  activeTabId: string | null;
  addTab: (opts?: { initialCommand?: string; title?: string }) => string;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  renameTab: (id: string, title: string) => void;
  setTabSessionId: (tabId: string, sessionId: string) => void;
}

const DockContext = createContext<DockContextValue | null>(null);

const HEIGHT_KEY = "terminal:dock-height";
const DEFAULT_HEIGHT = 280;
const MIN_HEIGHT = 180;
const MAX_HEIGHT = 560;

function makeTabId() {
  return `t_${Math.random().toString(36).slice(2, 10)}`;
}

function defaultTitle(index: number) {
  return `shell ${index}`;
}

export function DockProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [height, setHeightState] = useState(DEFAULT_HEIGHT);
  const [tabs, setTabs] = useState<TerminalTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const counterRef = useRef(0);

  useEffect(() => {
    const saved = Number(localStorage.getItem(HEIGHT_KEY));
    if (Number.isFinite(saved) && saved >= MIN_HEIGHT && saved <= MAX_HEIGHT) {
      setHeightState(saved);
    }
  }, []);

  const setHeight = useCallback((h: number) => {
    const clamped = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.round(h)));
    setHeightState(clamped);
    try { localStorage.setItem(HEIGHT_KEY, String(clamped)); } catch { /* ignore */ }
  }, []);

  const addTab = useCallback<DockContextValue["addTab"]>((opts) => {
    const id = makeTabId();
    counterRef.current += 1;
    const next: TerminalTab = {
      id,
      sessionId: null,
      title: opts?.title ?? defaultTitle(counterRef.current),
      initialCommand: opts?.initialCommand,
      kind: deriveKind(opts?.initialCommand),
      startedAt: Date.now(),
    };
    setTabs((prev) => [...prev, next]);
    setActiveTabId(id);
    return id;
  }, []);

  const removeTab = useCallback((id: string) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx < 0) return prev;
      const next = prev.filter((t) => t.id !== id);
      setActiveTabId((cur) => {
        if (cur !== id) return cur;
        if (next.length === 0) return null;
        // Activate the neighbor on the left, or the new first.
        const fallback = next[Math.max(0, idx - 1)] ?? next[0];
        return fallback ? fallback.id : null;
      });
      return next;
    });
  }, []);

  const setActiveTab = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const renameTab = useCallback((id: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, title: trimmed } : t)));
  }, []);

  const setTabSessionId = useCallback((tabId: string, sessionId: string) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId && t.sessionId !== sessionId ? { ...t, sessionId } : t)),
    );
  }, []);

  // Seed the dock's first tab (a running `claude`) when it opens empty — shared
  // by the user-menu toggle and the `?dock=terminal` deep-link opener so both
  // land on the same first-run terminal. A no-op once any tab exists (reads
  // `prev`, not a captured `tabs`, so it can't be stale inside an updater).
  const seedIfEmpty = useCallback(() => {
    setTabs((prev) => {
      if (prev.length > 0) return prev;
      const id = makeTabId();
      counterRef.current = 1;
      setActiveTabId(id);
      return [
        {
          id,
          sessionId: null,
          title: "claude",
          initialCommand: "claude",
          kind: "agent",
          startedAt: Date.now(),
        },
      ];
    });
  }, []);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      // First time opening with no tabs: seed one running `claude`.
      if (next) seedIfEmpty();
      return next;
    });
  }, [seedIfEmpty]);

  // Explicitly OPEN the dock (idempotent — never flips it shut, unlike toggle).
  // Target of the menu-bar "Open in Terminal" deep link (`/?dock=terminal`),
  // which must open the terminal on an app that may already have the dock up.
  const openTerminal = useCallback(() => {
    setOpen(true);
    seedIfEmpty();
  }, [seedIfEmpty]);

  const value = useMemo<DockContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      openTerminal,
      height,
      setHeight,
      tabs,
      activeTabId,
      addTab,
      removeTab,
      setActiveTab,
      renameTab,
      setTabSessionId,
    }),
    [open, toggle, openTerminal, height, setHeight, tabs, activeTabId, addTab, removeTab, setActiveTab, renameTab, setTabSessionId],
  );

  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
}

export function useDock(): DockContextValue {
  const ctx = useContext(DockContext);
  if (!ctx) throw new Error("useDock must be used within DockProvider");
  return ctx;
}
